import { Linking, View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { useTheme } from '@ui-kitten/components';
import dayjs from 'dayjs';
import lodashIsEmpty from 'lodash/isEmpty';

import Button from '../../../Components/Button';
import ListItem from '../../../Components/ListItem';
import MapSnapShotPreview from '../../../Components/MapSnapshotPreview';
import { textCategory } from '../../../Components/Reservation/constants';
import ResponsiveText from '../../../Components/ResponsiveText';
import constants from '../../../Config/constants';
import DateHelper from '../../../Helper/Date';
import { useGetTableReservableDaysQuery } from '../../../RTK/api/reservations';
import Service from '../../../Service';
import themeStyle, { globalSpacing } from '../../../Theme/styles';
import breakpoints from '../../../Theme/styles/breakpoints';
import { DBDATE } from '../../../types/date';

type MapsAndInfoProps = {
  latitude: number;
  location_address: string;
  longitude: number;
  name: string;
  storeId?: string;
};

const MapsAndInfo = ({
  latitude,
  location_address,
  longitude,
  name,
  storeId,
}: MapsAndInfoProps) => {
  const theme = useTheme();

  const {
    data: tableReservableDates,
    isFetching: isTableReservableDatesLoading,
  } = useGetTableReservableDaysQuery({ payload: storeId }, { skip: !storeId });

  const _goToEventLocation = () => {
    const url = Service.getStoreMapUrl(name, latitude, longitude);
    Linking.openURL(url);
  };

  return (
    <View
      dataSet={{ media: ids.locAndHoursContainer }}
      style={[
        themeStyle.flex1,
        themeStyle.pageVerticalSpacing,
        styles.locAndHoursContainer,
      ]}
    >
      <View style={themeStyle.flex1}>
        <Button onPress={_goToEventLocation} plain>
          <MapSnapShotPreview
            height={350}
            width={350}
            markers={[{ latitude, longitude }]}
          />
        </Button>
      </View>
      <View
        dataSet={{ media: ids.locationContainer }}
        style={styles.locationContainer}
      >
        <View
          style={[
            themeStyle.pageVerticalSpacingMedium,
            themeStyle.pageHorizontalSpacingSmall,
            {
              borderRadius: 4,
              borderWidth: 1,
              borderColor: theme['color-info-500'],
            },
          ]}
        >
          <View style={[themeStyle.flex1, themeStyle.spacingBottomMedium]}>
            <Button onPress={_goToEventLocation} plain>
              <ListItem
                leftIcon={{
                  name: 'pin',
                  color: theme['color-info-500'],
                  size: 20,
                }}
                title={
                  <ResponsiveText
                    category={textCategory.BODY}
                    status="info"
                    style={themeStyle.underline}
                  >
                    {location_address}
                  </ResponsiveText>
                }
              />
            </Button>
          </View>
          {!isTableReservableDatesLoading &&
            !lodashIsEmpty(tableReservableDates?.dining_service_group) && (
              <ListItem
                leftIcon={{
                  name: 'clock',
                  color: theme['color-info-500'],
                  size: 20,
                }}
                title={
                  <View
                    style={[
                      themeStyle.flexDirectionRowCenter,
                      themeStyle.flexWrap,
                    ]}
                  >
                    <ResponsiveText
                      category={textCategory.BODY}
                      status="primary"
                    >
                      {[
                        `${
                          tableReservableDates.blockout_dates.includes(
                            dayjs().format(constants.DBDATE_FORMAT) as DBDATE,
                          )
                            ? 'Closed'
                            : 'Open'
                        } today from `,
                        ...tableReservableDates.dining_service_group,
                      ].map((dsg, dsgIndex) => {
                        if (typeof dsg === 'string') return dsg;
                        const isLast =
                          dsgIndex ===
                          tableReservableDates.dining_service_group.length;
                        const separator =
                          dsgIndex ===
                          tableReservableDates.dining_service_group.length - 1
                            ? ' and '
                            : isLast
                            ? ''
                            : ', ';
                        return `${DateHelper.getDayjsAtTime(
                          dsg.start_time,
                        ).format(
                          constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
                        )} to ${DateHelper.getDayjsAtTime(dsg.end_time).format(
                          constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
                        )}${separator}`;
                      })}
                    </ResponsiveText>
                  </View>
                }
              />
            )}
        </View>
      </View>
    </View>
  );
};

export const { ids, styles } = StyleSheet.create({
  locAndHoursContainer: {
    ...themeStyle.flexDirectionCol,
    [`@media ${breakpoints.md}`]: {
      ...themeStyle.flexDirectionRow,
    },
  },
  locationContainer: {
    marginLeft: 0,
    marginTop: globalSpacing,
    [`@media ${breakpoints.md}`]: {
      flex: 1.5,
      marginTop: 0,
      marginLeft: globalSpacing,
    },
  },
});

export default MapsAndInfo;
